import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function MenuSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const categories = content.categories || [];
    const currency = content.currency || 'S/';
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'classic';
    const [activeCategory, setActiveCategory] = useState(0);

    if (categories.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const renderItem = (item, i) => (
        <div key={i} className={`flex justify-between items-start gap-4 py-4 ${i > 0 ? 'border-t' : ''} transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
            style={{ ...(i > 0 ? { borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' } : {}), transitionDelay: visible ? `${200 + i * 60}ms` : '0ms' }}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm" style={{ color: s.text }}>{item.name}</h4>
                    {item.badge && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: s.accent }}>{item.badge}</span>
                    )}
                </div>
                {item.description && <p className="text-xs mt-0.5" style={{ color: s.textSub }}>{item.description}</p>}
            </div>
            <div className="shrink-0 font-bold text-sm" style={{ color: s.accent }}>
                {currency} {item.price}
            </div>
        </div>
    );

    // ── Tabs variant ──
    if (variant === 'tabs') {
        const cat = categories[activeCategory];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Menu'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {categories.map((c, i) => (
                            <button key={i} onClick={() => setActiveCategory(i)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${i === activeCategory ? 'text-white shadow-lg' : ''}`}
                                style={i === activeCategory ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>
                                {c.name}
                            </button>
                        ))}
                    </div>
                    {cat && (
                        <div className={`${card} p-6 max-w-2xl mx-auto`}>
                            {(cat.items || []).map(renderItem)}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Menu'} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${categories.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
                        {categories.map((cat, ci) => (
                            <div key={ci} className={`${card} overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${ci * 150}ms` : '0ms' }}>
                                <div className="h-1.5" style={{ backgroundColor: s.accent }} />
                                <div className="p-5">
                                    <h3 className="font-bold text-base mb-4" style={{ color: s.text }}>{cat.name}</h3>
                                    {(cat.items || []).map(renderItem)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Classic (default) ──
    const cols = config.columns || 2;
    const gridCls = cols === 1 ? 'max-w-2xl mx-auto' : 'md:grid-cols-2';

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Menu'} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-x-12 gap-y-10`}>
                    {categories.map((cat, ci) => (
                        <div key={ci} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${ci * 150}ms` : '0ms' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="font-bold text-lg whitespace-nowrap" style={{ color: s.text }}>{cat.name}</h3>
                                <div className="flex-1 border-b border-dashed" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb' }} />
                            </div>
                            {(cat.items || []).map(renderItem)}
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
